import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Toast from '../../Toast';
import { PendingRows } from '../../Skeleton';
import {
  addTypedResponse,
  deleteTypedResponse,
  registerIngestionFile,
  removeIngestionFile,
} from '../../../service/exams.api';
import { useBulkFileUpload } from '../../../hooks/useBulkFileUpload';
import { BulkUploadWizardShell } from '../components/BulkUploadWizardShell';
import {
  TextTabIcon,
  UploadTabIcon,
} from '../components/shared/BulkUploadModeIcons';
import { FileDropArea } from '../components/shared/FileDropArea';
import {
  EmptyResponses,
  partitionFiles,
  warnAboutEmptyFiles,
  warnAboutInvalidFiles,
  warnAboutOversizedFiles,
} from '../components/BulkUploadDragDropStep';
import { getFileType } from '../utils/fileTypes';
import docIcon from '../../../static/img/doc.svg';
import pdfIcon from '../../../static/img/pdf_logo.svg';
import imageIcon from '../../../static/img/no-image.svg';
import removeFileIcon from '../../../static/icons/red-cross.svg';
import {
  BulkUploadResponsesPanel,
  BulkUploadPagesPanel,
  BulkUploadPagesHeader,
  BulkUploadPagesCount,
  BulkUploadDropArea,
  BulkUploadTab,
  BulkUploadTabRow,
  BulkUploadHelperText,
  BulkUploadFilesSection,
  BulkUploadFilesHeading,
  BulkUploadFileList,
  BulkUploadFileRow,
  BulkUploadFileIcon,
  BulkUploadFileMeta,
  BulkUploadFileName,
  BulkUploadFilePages,
  BulkUploadFileDelete,
  BulkUploadFailedRow,
  BulkUploadFailedReason,
  BulkUploadRetryButton,
  BulkUploadSectionTitle,
} from '../styles';
import { bulkJobKey } from './useBulkIngestionJob';
import { uploadBulkFile } from './bulkUploadTransport';
import { ingestionKeys } from '../../Exams/shared/keys';
import { CHECK_PAGES_COPY } from '../../ScriptIngestion/CheckPages/copy';

export const INTAKE_COPY = Object.freeze({
  title: 'Add responses',
  upload: 'Upload',
  manual: 'Manually add text',
  uploadHelp:
    'Upload photos, PDFs or Word documents containing student responses.',
  manualHelp: 'Type or paste student responses to add them as pages',
  uploadedFiles: 'Uploaded files',
  uploading: 'Uploading…',
  reading: 'Reading…',
  uploaded: 'Uploaded',
  failedUploads: 'Failed uploads',
  fileUnreadable: "We couldn't read this file",
  typedHeading: 'Typed responses',
  nameLabel: 'Response name',
  nameOptional: '(optional)',
  nameHelp: 'Add response name to easily identify this page',
  namePlaceholder: 'Page Title',
  studentLabel: 'Student',
  studentNone: 'Not sure yet',
  responseLabel: 'Response',
  responsePlaceholder: 'Type your response here...',
  addTyped: 'Add to pages',
  typedFailed: "We couldn't add that response. Try again.",
  removeFailed: "We couldn't remove that. Try again.",
  pages: (n) => `${n} ${n === 1 ? 'page' : 'pages'}`,
  words: (n) => `${n} ${n === 1 ? 'word' : 'words'}`,
  unnamedTyped: (n) => `Typed response ${n}`,
});

const iconFor = (name) => {
  const type = getFileType(name || '');
  if (type === 'pdf') return pdfIcon;
  if (type === 'image') return imageIcon;
  return docIcon;
};

const IntakeLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.75fr) minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  flex: 1;
  min-height: 0;

  > ${BulkUploadResponsesPanel}, > ${BulkUploadPagesPanel} {
    width: auto;
    min-width: 0;
    overflow-y: auto;
  }

  > ${BulkUploadResponsesPanel} {
    gap: 24px;
  }

  > ${BulkUploadPagesPanel} {
    border-left: 0;
  }

  ${BulkUploadDropArea} {
    min-height: 230px;
    justify-content: center;
  }

  ${BulkUploadFileMeta} {
    flex-direction: column;
    align-items: stretch;
    gap: 2px;
  }

  ${BulkUploadFilePages} {
    font-size: 14px;
  }

  @media (max-width: 768px) {
    display: block;
    overflow-y: auto;

    > ${BulkUploadResponsesPanel}, > ${BulkUploadPagesPanel} {
      overflow: visible;
    }

    > ${BulkUploadPagesPanel} {
      border-top: 0;
    }
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResponseField = styled(FieldGroup)`
  flex: 1 0 auto;
`;

const FieldLabel = styled.label`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #1e252a);

  span {
    font-weight: 400;
    color: var(--text-secondary, #6b7280);
  }
`;

const FieldHelper = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--text-secondary, #6b7280);
`;

const fieldStyles = `
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(201, 198, 204, 0.85);
  border-radius: 8px;
  padding: 10px 12px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  color: var(--text-primary, #1e252a);
  background: #fff;
`;

const TextInput = styled.input`
  ${fieldStyles}
`;

const Select = styled.select.attrs({ className: 'inset-select-arrow' })`
  ${fieldStyles}
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  flex: 1 0 auto;
  min-height: 180px;
  resize: vertical;
`;

const AddButton = styled.button`
  height: 48px;
  margin-top: 8px;
  padding: 0 24px;
  border-radius: 999px;
  border: none;
  background: var(--light-mode-purple, #7200e0);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--error-red, #c62828);
`;

/** The job's live files (not removed), oldest first. */
export const liveFiles = (ws) =>
  (ws?.files || []).filter((file) => file && file.status !== 'REMOVED');

/** The job's typed pages, in upload order. */
export const typedPages = (ws) =>
  Object.values(ws?.pages || {})
    .filter((page) => page?.source === 'TYPED')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/** Prefer the response's own name, including while it is a loose page. */
export const typedLabel = (ws, page, index) =>
  page?.typedName?.trim() ||
  ws?.scripts?.[page?.scriptId]?.name?.trim() ||
  INTAKE_COPY.unnamedTyped(index + 1);

/** A typed response's word count, from its script (or the page, if it carries one). */
const typedWords = (ws, page) => {
  const words = ws?.scripts?.[page?.scriptId]?.typedWords ?? page?.typedWords;
  return Number.isFinite(words) ? words : null;
};

/**
 * A file row's subline: its page count once known; "Reading…" only while the
 * file is being read (EXPLODING, or the job has started — files added after
 * Continue are read straight away); before Continue, a neutral "Uploaded".
 */
export const fileSub = (file, jobStatus) => {
  if (file.status === 'FAILED' || file.error) return INTAKE_COPY.fileUnreadable;
  const pages = file.pages ?? file.pageCountExact ?? null;
  if (pages) return INTAKE_COPY.pages(pages);
  const reading =
    file.status === 'EXPLODING' || (Boolean(jobStatus) && jobStatus !== 'OPEN');
  return reading ? INTAKE_COPY.reading : INTAKE_COPY.uploaded;
};

/** Roster entries a typed response can go to, grouped by class label. */
const rosterGroups = (roster) => {
  const groups = new Map();
  (roster || []).forEach((entry) => {
    if (!entry?.rosterId) return;
    const label = entry.classLabel || '';
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(entry);
  });
  return [...groups.entries()];
};

/**
 * Add responses on Bulk Upload v2 (build doc 06 §7.3): the v1 drop zone and
 * manual-text tab over the ingestion job. Files take the v1 handwritten
 * route and are registered with the job (HEIC → JPEG first, caps and accept
 * list as v1); typed responses go to POST /ingestion/{jobId}/typed, straight
 * into a student's response when one is picked. Nothing is rendered with
 * pdf.js: page counts come from the job once files are exploded.
 */
export default function BulkUploadIntakeV2({
  jobId,
  ws,
  roster = [],
  steps,
  currentStep,
  onStepClick,
  onBack,
  onClose,
  onContinue,
  isStarting = false,
  resolving = false,
  notice,
  startError = null,
  fullPage = false,
  uploadFile = uploadBulkFile,
  ...shellProps
}) {
  const queryClient = useQueryClient();
  const inputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState([]);
  const [retrying, setRetrying] = useState(() => new Set());
  const [who, setWho] = useState('');
  const [rosterId, setRosterId] = useState('');
  const [text, setText] = useState('');
  const [typedBusy, setTypedBusy] = useState(false);
  const [typedError, setTypedError] = useState(null);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: bulkJobKey(jobId) });
    queryClient.invalidateQueries({ queryKey: ingestionKeys.workspace(jobId) });
  }, [queryClient, jobId]);

  const uploadOne = useCallback(
    async (file) => {
      const uploaded = await uploadFile(file);
      await registerIngestionFile(jobId, uploaded.uploadedFileId);
      return uploaded;
    },
    [uploadFile, jobId]
  );
  const { uploadAll, retryUpload, dismissUpload, failedUploads } =
    useBulkFileUpload({ uploadFile: uploadOne });

  const pending = !jobId || !ws;
  const addFiles = async (fileList) => {
    if (pending) return;
    const { validFiles, invalidFileNames, oversizedFiles, emptyFiles } =
      partitionFiles(Array.from(fileList || []));
    warnAboutInvalidFiles(invalidFileNames);
    warnAboutOversizedFiles(oversizedFiles);
    warnAboutEmptyFiles(emptyFiles);
    if (!validFiles.length) return;
    const rows = validFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
    }));
    setUploading((current) => [...current, ...rows]);
    try {
      await uploadAll(validFiles);
    } finally {
      const ids = new Set(rows.map((row) => row.id));
      setUploading((current) => current.filter((row) => !ids.has(row.id)));
      refresh();
    }
  };

  const retryFailed = async (upload) => {
    setRetrying((current) => new Set(current).add(upload.id));
    try {
      await retryUpload(upload);
    } finally {
      setRetrying((current) => {
        const next = new Set(current);
        next.delete(upload.id);
        return next;
      });
      refresh();
    }
  };

  const removeFile = async (fileId) => {
    try {
      await removeIngestionFile(jobId, fileId);
    } catch (error) {
      toast(<Toast message={error?.message || INTAKE_COPY.removeFailed} />);
    }
    refresh();
  };

  const removeTyped = async (pageId) => {
    try {
      await deleteTypedResponse(jobId, pageId);
    } catch (error) {
      toast(<Toast message={error?.message || INTAKE_COPY.removeFailed} />);
    }
    refresh();
  };

  const addTyped = async () => {
    const body = text.trim();
    if (pending || !body || typedBusy) return;
    setTypedBusy(true);
    setTypedError(null);
    try {
      await addTypedResponse(jobId, {
        who: who.trim(),
        questionId: null,
        text: body,
        rosterId: rosterId || null,
      });
      setWho('');
      setRosterId('');
      setText('');
      refresh();
    } catch (error) {
      setTypedError(error?.message || INTAKE_COPY.typedFailed);
    } finally {
      setTypedBusy(false);
    }
  };

  const files = liveFiles(ws);
  const typed = typedPages(ws);
  const groups = useMemo(() => rosterGroups(roster), [roster]);
  const hasFiles = files.length > 0 || uploading.length > 0;
  const isUploading = uploading.length > 0;
  const nothingAdded = files.length === 0 && typed.length === 0;

  return (
    <BulkUploadWizardShell
      {...shellProps}
      steps={steps}
      currentStep={currentStep}
      onStepClick={onStepClick}
      showBack={Boolean(onBack)}
      onBack={onBack}
      isBackDisabled={isUploading}
      onClose={onClose}
      onContinue={onContinue}
      isContinueDisabled={pending || isUploading || isStarting || nothingAdded}
      fullPage={fullPage}
      data-component="bulk-upload-intake-v2"
      data-total-files={pending ? undefined : files.length}
      data-typed-count={pending ? undefined : typed.length}
      data-active-tab={activeTab}
    >
      <IntakeLayout>
        <BulkUploadResponsesPanel>
          <BulkUploadSectionTitle>{INTAKE_COPY.title}</BulkUploadSectionTitle>
          <BulkUploadTabRow>
            <BulkUploadTab
              type="button"
              disabled={resolving}
              aria-pressed={activeTab === 'upload'}
              $active={activeTab === 'upload'}
              onClick={() => setActiveTab('upload')}
            >
              <UploadTabIcon />
              {INTAKE_COPY.upload}
            </BulkUploadTab>
            <BulkUploadTab
              type="button"
              disabled={resolving}
              aria-pressed={activeTab === 'manual'}
              $active={activeTab === 'manual'}
              onClick={() => setActiveTab('manual')}
            >
              <TextTabIcon />
              {INTAKE_COPY.manual}
            </BulkUploadTab>
          </BulkUploadTabRow>
          <BulkUploadHelperText>
            {activeTab === 'upload'
              ? INTAKE_COPY.uploadHelp
              : INTAKE_COPY.manualHelp}
          </BulkUploadHelperText>

          {activeTab === 'upload' ? (
            <FileDropArea
              disabled={pending}
              inputRef={inputRef}
              hasFiles={hasFiles}
              isDragging={isDragging}
              onDraggingChange={setIsDragging}
              onFiles={addFiles}
            />
          ) : (
            <>
              <FieldGroup>
                <FieldLabel htmlFor="bulk-v2-typed-who">
                  {INTAKE_COPY.nameLabel}{' '}
                  <span>{INTAKE_COPY.nameOptional}</span>
                </FieldLabel>
                <FieldHelper>{INTAKE_COPY.nameHelp}</FieldHelper>
                <TextInput
                  id="bulk-v2-typed-who"
                  type="text"
                  placeholder={INTAKE_COPY.namePlaceholder}
                  value={who}
                  onChange={(e) => setWho(e.target.value)}
                />
              </FieldGroup>
              <FieldGroup>
                <FieldLabel htmlFor="bulk-v2-typed-student">
                  {INTAKE_COPY.studentLabel}{' '}
                  <span>{INTAKE_COPY.nameOptional}</span>
                </FieldLabel>
                <Select
                  id="bulk-v2-typed-student"
                  disabled={!groups.length}
                  value={rosterId}
                  onChange={(e) => setRosterId(e.target.value)}
                >
                  <option value="">{INTAKE_COPY.studentNone}</option>
                  {groups.map(([label, entries]) => {
                    const options = entries.map((entry) => (
                      <option
                        key={entry.rosterId}
                        value={entry.rosterId}
                        disabled={Boolean(entry.excludedReason)}
                      >
                        {entry.name || entry.studentId}
                        {entry.resubmission && !entry.excludedReason
                          ? ` · ${CHECK_PAGES_COPY.resubmissionRequested}`
                          : ''}
                      </option>
                    ));
                    return label ? (
                      <optgroup key={label} label={label}>
                        {options}
                      </optgroup>
                    ) : (
                      options
                    );
                  })}
                </Select>
              </FieldGroup>
              <ResponseField>
                <FieldLabel htmlFor="bulk-v2-typed-text">
                  {INTAKE_COPY.responseLabel}
                </FieldLabel>
                <TextArea
                  id="bulk-v2-typed-text"
                  placeholder={INTAKE_COPY.responsePlaceholder}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <AddButton
                  type="button"
                  disabled={pending || !text.trim() || typedBusy}
                  onClick={addTyped}
                >
                  {INTAKE_COPY.addTyped}
                </AddButton>
                {typedError && <ErrorText role="alert">{typedError}</ErrorText>}
              </ResponseField>
            </>
          )}

          {notice}
          {startError && <ErrorText role="alert">{startError}</ErrorText>}
        </BulkUploadResponsesPanel>
        <BulkUploadPagesPanel aria-label="Added responses">
          <BulkUploadPagesHeader>
            <BulkUploadSectionTitle $plain>
              Added files and text
            </BulkUploadSectionTitle>
            {!pending && (
              <BulkUploadPagesCount>
                {files.length + typed.length}
              </BulkUploadPagesCount>
            )}
          </BulkUploadPagesHeader>
          <BulkUploadHelperText>
            You can organise the pages and assign them to students in the next
            step.
          </BulkUploadHelperText>
          {pending && <PendingRows aria-label="Loading added responses" />}
          {!pending &&
            nothingAdded &&
            !isUploading &&
            !failedUploads.length && (
              <EmptyResponses title="No responses added" />
            )}
          {hasFiles && (
            <BulkUploadFilesSection>
              <BulkUploadFilesHeading>
                {INTAKE_COPY.uploadedFiles}
              </BulkUploadFilesHeading>
              <BulkUploadFileList data-testid="bulk-v2-file-list">
                {files.map((file) => (
                  <BulkUploadFileRow
                    key={file.fileId}
                    data-testid="bulk-v2-file"
                  >
                    <BulkUploadFileIcon src={iconFor(file.name)} alt="" />
                    <BulkUploadFileMeta>
                      <BulkUploadFileName>{file.name}</BulkUploadFileName>
                      <BulkUploadFilePages>
                        {fileSub(file, ws?.status)}
                      </BulkUploadFilePages>
                    </BulkUploadFileMeta>
                    <BulkUploadFileDelete
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => removeFile(file.fileId)}
                    >
                      <img src={removeFileIcon} alt="" width={16} height={16} />
                    </BulkUploadFileDelete>
                  </BulkUploadFileRow>
                ))}
                {uploading.map((row) => (
                  <BulkUploadFileRow
                    key={row.id}
                    data-testid="bulk-v2-uploading"
                  >
                    <BulkUploadFileIcon src={iconFor(row.name)} alt="" />
                    <BulkUploadFileMeta>
                      <BulkUploadFileName>{row.name}</BulkUploadFileName>
                      <BulkUploadFilePages>
                        {INTAKE_COPY.uploading}
                      </BulkUploadFilePages>
                    </BulkUploadFileMeta>
                  </BulkUploadFileRow>
                ))}
              </BulkUploadFileList>
            </BulkUploadFilesSection>
          )}
          {failedUploads.length > 0 && (
            <BulkUploadFilesSection>
              <BulkUploadFilesHeading>
                {INTAKE_COPY.failedUploads}
              </BulkUploadFilesHeading>
              <BulkUploadFileList>
                {failedUploads.map((upload) => (
                  <BulkUploadFailedRow key={upload.id}>
                    <BulkUploadFileIcon src={iconFor(upload.name)} alt="" />
                    <BulkUploadFileMeta>
                      <BulkUploadFileName>{upload.name}</BulkUploadFileName>
                      <BulkUploadFailedReason>
                        {upload.error}
                      </BulkUploadFailedReason>
                    </BulkUploadFileMeta>
                    <BulkUploadRetryButton
                      type="button"
                      aria-label={`Retry ${upload.name}`}
                      disabled={retrying.has(upload.id)}
                      onClick={() => retryFailed(upload)}
                    >
                      {retrying.has(upload.id) ? 'Retrying...' : 'Retry'}
                    </BulkUploadRetryButton>
                    <BulkUploadFileDelete
                      type="button"
                      aria-label={`Dismiss ${upload.name}`}
                      disabled={retrying.has(upload.id)}
                      onClick={() => dismissUpload(upload.id)}
                    >
                      <img src={removeFileIcon} alt="" width={16} height={16} />
                    </BulkUploadFileDelete>
                  </BulkUploadFailedRow>
                ))}
              </BulkUploadFileList>
            </BulkUploadFilesSection>
          )}
          {typed.length > 0 && (
            <BulkUploadFilesSection>
              <BulkUploadFilesHeading>
                {INTAKE_COPY.typedHeading}
              </BulkUploadFilesHeading>
              <BulkUploadFileList data-testid="bulk-v2-typed-list">
                {typed.map((page, index) => (
                  <BulkUploadFileRow key={page.pageId}>
                    <BulkUploadFileIcon src={docIcon} alt="" />
                    <BulkUploadFileMeta>
                      <BulkUploadFileName>
                        {typedLabel(ws, page, index)}
                      </BulkUploadFileName>
                      {typedWords(ws, page) !== null && (
                        <BulkUploadFilePages>
                          {INTAKE_COPY.words(typedWords(ws, page))}
                        </BulkUploadFilePages>
                      )}
                    </BulkUploadFileMeta>
                    <BulkUploadFileDelete
                      type="button"
                      aria-label={`Remove ${typedLabel(ws, page, index)}`}
                      onClick={() => removeTyped(page.pageId)}
                    >
                      <img src={removeFileIcon} alt="" width={16} height={16} />
                    </BulkUploadFileDelete>
                  </BulkUploadFileRow>
                ))}
              </BulkUploadFileList>
            </BulkUploadFilesSection>
          )}
        </BulkUploadPagesPanel>
      </IntakeLayout>
    </BulkUploadWizardShell>
  );
}
